/* The cohort filter builder, ONAM skin (18 Oct 2026, night).

   MAP's builder picked one attribute at a time from a long typeahead list (every attribute of every selected dataset in
   one flowing list), then an operator, a value and a logical operator, then "Add condition". Here:
   - "Add attributes" opens a panel: every attribute, grouped by dataset when there is more than one, in a grid you can
     search; tick the ones you want. Each ticked attribute is added straight away as its own condition.
   - Each condition sits in its own row with its own operator and value controls, sized to the attribute: Equals / Not
     equals for text, every comparison for numbers; buttons for up to four values, a list beyond that, a number field
     for numbers.
   - "Add a value" puts a second condition on the same attribute; the attribute's conditions are then joined by one
     AND / OR switch. Different attributes are always combined with AND (as MAP's builder did by default).

   It edits the page's own `conditions` (attribute, operator, value, logicalOperator), so the query sent is built exactly
   as before: per attribute, its conditions in order, the logical operator before the second and later ones. A row with
   no operator or value yet is incomplete; the page holds Search until every row is complete. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { OI } from "../../onam/mark";

export interface Condition {
  attribute: string;
  operator: string;
  value: string;
  logicalOperator: string;
  objective?: string;
  enrichmentAttributes?: string[];
}
interface Attr { name?: string; label?: string; type?: string; values?: unknown[]; dataset_name?: string }
interface Op { value: string; label: string }

const TEXT_TYPES = ["varchar", "string", "boolean"];
export const opsFor = (a: Attr | undefined, all: Op[]) => (TEXT_TYPES.includes((a?.type || "").toLowerCase()) ? all.filter((o) => o.value === "eq" || o.value === "neq") : all);

export default function ConditionBuilder({ attrs, conditions, setConditions, operators, showDatasets }: {
  attrs: Record<string, Attr>;
  conditions: Condition[];
  setConditions: (next: Condition[]) => void;
  operators: Op[];
  showDatasets: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const nameOf = (k: string) => attrs[k]?.label?.trim() || attrs[k]?.name || k;

  // the panel closes on Escape or a click outside it, and opens with the search field ready
  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const out = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", out); window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", out); window.removeEventListener("keydown", esc); };
  }, [open]);

  const picked = useMemo(() => new Set(conditions.map((c) => c.attribute)), [conditions]);
  // the panel's attributes, by dataset (in the order the datasets first appear), filtered by the search
  const groups = useMemo(() => {
    const m = new Map<string, string[]>();
    const term = q.trim().toLowerCase();
    Object.keys(attrs).forEach((k) => {
      if (term && !(nameOf(k).toLowerCase().includes(term) || (attrs[k]?.dataset_name || "").toLowerCase().includes(term))) return;
      const g = showDatasets ? attrs[k]?.dataset_name || "Other attributes" : "";
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(k);
    });
    return Array.from(m.entries());
  }, [attrs, q, showDatasets]); // eslint-disable-line react-hooks/exhaustive-deps

  const blank = (k: string, logical = ""): Condition => {
    const ops = opsFor(attrs[k], operators);
    // text attributes start on Equals (one click fewer); numbers ask for their comparison
    return { attribute: k, operator: ops.some((o) => o.value === "eq") && ops.length <= 2 ? "eq" : "", value: "", logicalOperator: logical };
  };
  const toggle = (k: string) => setConditions(picked.has(k) ? conditions.filter((c) => c.attribute !== k) : [...conditions, blank(k)]);
  const update = (i: number, patch: Partial<Condition>) => setConditions(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => {
    const k = conditions[i].attribute, next = conditions.filter((_, j) => j !== i);
    // the attribute's first remaining condition carries no joining operator
    const first = next.findIndex((c) => c.attribute === k);
    if (first >= 0 && next[first].logicalOperator) next[first] = { ...next[first], logicalOperator: "" };
    setConditions(next);
  };
  // another condition on the same attribute, right after its last one, joined like the others (OR by default)
  const addValue = (k: string) => {
    const idx = conditions.map((c) => c.attribute).lastIndexOf(k);
    const join = conditions.find((c) => c.attribute === k && c.logicalOperator)?.logicalOperator || "OR";
    const next = [...conditions];
    next.splice(idx + 1, 0, blank(k, join));
    setConditions(next);
  };
  const setJoin = (k: string, op: string) => {
    let seen = false;
    setConditions(conditions.map((c) => { if (c.attribute !== k) return c; if (!seen) { seen = true; return c; } return { ...c, logicalOperator: op }; }));
  };

  // the rows, grouped by attribute in the order the attributes were added
  const order = Array.from(new Set(conditions.map((c) => c.attribute)));
  const n = picked.size;

  return (
    <div className="nq-cb">
      <div className="nq-cb-top" ref={box}>
        <button type="button" className="nq-cb-add" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((x) => !x)}>
          <OI n="add" size={14} />{n ? "Add or remove attributes" : "Add attributes"}
        </button>
        <span className="nq-cb-count">{n ? `${n} ${n === 1 ? "attribute" : "attributes"} · ${conditions.length} ${conditions.length === 1 ? "condition" : "conditions"}` : "Pick the attributes to filter on. Each gets its own operator and value."}</span>
        {open ? (
          <div className="nq-cb-panel" role="dialog" aria-label="Choose attributes">
            <div className="nq-cb-ph">
              <label className="nq-cb-search"><OI n="search" size={14} /><input ref={search} type="search" placeholder="Search attributes" aria-label="Search attributes" value={q} onChange={(e) => setQ(e.target.value)} /></label>
              <span className="nq-cb-sel">{n} selected</span>
              <button type="button" className="nq-cb-done" onClick={() => setOpen(false)}>Done</button>
            </div>
            <div className="nq-cb-list">
              {groups.length ? groups.map(([g, ks]) => (
                <section key={g || "all"} className="nq-cb-grp" aria-label={g || "Attributes"}>
                  {g ? <h4 className="nq-cb-gh">{g}<span>{ks.length}</span></h4> : null}
                  <div className="nq-cb-grid">
                    {ks.map((k) => (
                      <label key={k} className={`nq-cb-item${picked.has(k) ? " on" : ""}`} title={nameOf(k)}>
                        <input type="checkbox" checked={picked.has(k)} onChange={() => toggle(k)} />
                        <span className="nq-cb-box" aria-hidden="true">{picked.has(k) ? <OI n="check" size={12} /> : null}</span>
                        <span className="nq-cb-nm">{nameOf(k)}</span>
                      </label>))}
                  </div>
                </section>)) : <p className="nq-cb-none">No attribute matches “{q}”.</p>}
            </div>
          </div>) : null}
      </div>

      {order.length ? (
        <ol className="nq-cb-rows">
          {order.map((k, gi) => {
            const rows = conditions.map((c, i) => ({ c, i })).filter((x) => x.c.attribute === k);
            const a = attrs[k], ops = opsFor(a, operators), vals = (a?.values || []).map(String), isInt = (a?.type || "").toLowerCase() === "int";
            const join = rows.find((x) => x.c.logicalOperator)?.c.logicalOperator || "OR";
            return (
              <li key={k} className="nq-cb-attr">
                {gi ? <span className="nq-cb-and" aria-label="and">AND</span> : null}
                <div className="nq-cb-ah">
                  <b title={nameOf(k)}>{nameOf(k)}</b>
                  {showDatasets && a?.dataset_name ? <span className="nq-cb-ds">{a.dataset_name}</span> : null}
                  {rows.length > 1 ? (
                    <span className="nq-cb-join" role="group" aria-label={`Join ${nameOf(k)}'s values with`}>
                      {["OR", "AND"].map((op) => <button key={op} type="button" aria-pressed={join === op} onClick={() => setJoin(k, op)}>{op === "OR" ? "Any of these" : "All of these"}</button>)}
                    </span>) : null}
                </div>
                {rows.map(({ c, i }, ri) => (
                  <div key={i} className={`nq-cb-row${!c.operator || !c.value ? " todo" : ""}`}>
                    <span className="nq-cb-j" aria-hidden="true">{ri ? join : ""}</span>
                    {ops.length <= 3 ? (
                      <span className="nq-cb-seg" role="group" aria-label="Operator">
                        {ops.map((o) => <button key={o.value} type="button" aria-pressed={c.operator === o.value} onClick={() => update(i, { operator: o.value })}>{o.label}</button>)}
                      </span>
                    ) : (
                      <select className="nq-cb-sel-op" aria-label="Operator" value={c.operator} onChange={(e) => update(i, { operator: e.target.value })}>
                        <option value="">Operator</option>{ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>)}
                    {isInt ? (
                      <input className="nq-cb-num" type="number" aria-label="Value" placeholder="Number" value={c.value} onChange={(e) => update(i, { value: e.target.value })} />
                    ) : vals.length && vals.length <= 4 ? (
                      <span className="nq-cb-seg" role="group" aria-label="Value">
                        {vals.map((v) => <button key={v} type="button" aria-pressed={c.value === v} onClick={() => update(i, { value: v })}>{v}</button>)}
                      </span>
                    ) : vals.length ? (
                      <select className="nq-cb-sel-val" aria-label="Value" value={c.value} onChange={(e) => update(i, { value: e.target.value })}>
                        <option value="">Value</option>{vals.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    ) : (
                      <input className="nq-cb-num" type="text" aria-label="Value" placeholder="Value" value={c.value} onChange={(e) => update(i, { value: e.target.value })} />)}
                    <button type="button" className="nq-cb-x" title="Remove this condition" aria-label={`Remove this condition on ${nameOf(k)}`} onClick={() => remove(i)}><OI n="close" size={14} /></button>
                  </div>))}
                <button type="button" className="nq-cb-more" onClick={() => addValue(k)}><OI n="add" size={12} />Add a value</button>
              </li>);
          })}
        </ol>
      ) : (
        <p className="nq-cb-empty">No conditions yet. Add the attributes you want to filter on.</p>
      )}
    </div>
  );
}
